import { DEVICE, VERSION_CAN_RUN_MCT } from '@/shared/config';
import _ from 'lodash';
import { t } from 'ttag';
import type { AmplifyEcgSignal, Device, DeviceOption } from './device.types';
import { DEVICE_STATUS } from './deviceConstants';
import { checkUsingHighSamplingRateDevice } from './checkUsingHighSamplingRateDevice';

/**
 * Filters the device select options against the text typed by the user. Matches the option's precomputed filter label with a substring test and excludes any option that has no such label.
 *
 * @param inputValue - Text typed into the device search.
 * @param option - Device option being tested.
 * @returns True when the option's filter label contains the input.
 */
const filterOption = (inputValue: string, option: DeviceOption | null) => {
  if (option?.filterLabel) {
    return option.filterLabel.includes(inputValue);
  }
  return false;
};

/**
 * Works out which status tags a device row should show, keeping them mutually exclusive by priority. Derives the RMA, offline and high-sampling-rate conditions from the device's additional status, status and last sync schema, then combines them with the renewal, running-study and maintenance flags so renewal suppresses the others and RMA or a running study hide the offline, sampling-rate and maintenance tags.
 *
 * @param device - Device or device option whose tags are computed.
 * @returns Flags for the high sampling rate, maintenance needed, RMA, renewal, offline and in-study tags.
 */
const checkDisplayTags = (device: Device | DeviceOption) => {
  const { isRenewal, isRunningStudy, shouldMaintain, additionalStatus, lastSync, status } = device;
  const isRma = additionalStatus === 'RMA';
  const isOffline = status === DEVICE_STATUS.OFFLINE;
  const isHighSamplingRate = checkUsingHighSamplingRateDevice(lastSync?.schema);
  const shouldDisplayHsrTag =
    isHighSamplingRate && !isRma && !isRenewal && !isOffline && !isRunningStudy;
  const shouldDisplayMaintenanceNeededTag =
    shouldMaintain && !isRenewal && !isOffline && !isRunningStudy;
  const shouldDiplayRmaTag = isRma && !isRenewal;
  const shouldDisplayRenewalTag = isRenewal;
  const shouldDisplayOfflineTag = isOffline && !isRma && !isRenewal && !isRunningStudy;
  const shouldDisplayInStudyTag = isRunningStudy && !isRma && !isRenewal;
  return {
    shouldDisplayHsrTag,
    shouldDisplayMaintenanceNeededTag,
    shouldDiplayRmaTag,
    shouldDisplayRenewalTag,
    shouldDisplayOfflineTag,
    shouldDisplayInStudyTag,
  };
};

/**
 * Explains why a device cannot start a new study, as modal content. Reads the device's display tags and returns an opened error modal with renewal, in-study or RMA copy, the RMA case skipped for test facilities, or an offline message worded per model for Bioflux, Biotres Pro and the rest, and otherwise reports no error.
 *
 * @param option - Selected device option being checked.
 * @returns Whether to open the device error modal and the message to show in it.
 */
const checkDeviceError = (option: DeviceOption) => {
  const {
    shouldDisplayRenewalTag,
    shouldDisplayInStudyTag,
    shouldDiplayRmaTag,
    shouldDisplayOfflineTag,
  } = checkDisplayTags(option);

  if (shouldDisplayRenewalTag) {
    return {
      isOpenDeviceErrorModal: true,
      deviceErrorModalMessage: t`The Renewal device is used to replace a broken device. You can initiate an RMA ticket for a broken device and select this Renewal device as a replacement. Then, this device will be available for you to start a new study.`,
    };
  }

  if (shouldDisplayInStudyTag) {
    return {
      isOpenDeviceErrorModal: true,
      deviceErrorModalMessage: t`This device is currently running another study. Please ensure the study has ended or been aborted before starting a new one.`,
    };
  }

  if (shouldDiplayRmaTag) {
    const { facility } = option;
    if (facility.status !== 'Test') {
      return {
        isOpenDeviceErrorModal: true,
        deviceErrorModalMessage: t`This device belongs to an RMA ticket. You cannot start a study with this device until the RMA process is completed.`,
      };
    }
  }

  if (shouldDisplayOfflineTag || option.status === DEVICE_STATUS.OFFLINE) {
    const deviceErrorModalMessage =
      option.model === DEVICE.BIOFLUX
        ? t`This device is offline. Please ensure the device is on and your area has good cellular coverage.`
        : option.model === DEVICE.BIOTRES_PRO
          ? t`This device is offline. Please ensure the device is on and your area has good cellular coverage. You can also connect your device with the Diagnostics app via Bluetooth/USB to ensure it is online and ready to start a new study.`
          : t`This device is offline. Please connect the device with the Diagnostics app via Bluetooth/USB to ensure it is online and ready to start a new study.`;
    return {
      isOpenDeviceErrorModal: true,
      deviceErrorModalMessage,
    };
  }

  return {
    isOpenDeviceErrorModal: false,
    deviceErrorModalMessage: '',
  };
};

/**
 * Decides whether the MCT modal may be shown for a device firmware schema. Returns true only when the schema is at or below the highest version able to run MCT.
 *
 * @param schema - Firmware schema version of the device.
 * @returns True when the schema still supports MCT.
 */
const checkCanShowModal = (schema: number) => {
  if (schema <= VERSION_CAN_RUN_MCT) {
    return true;
  }
  return false;
};

/**
 * Turns a facility's devices into select options for starting a study, with status tags in each
 * label. Stamps display tags and an order index onto each device, sorts ready devices first and
 * maintenance-needed ones next, then maps each to a JSX label with its auto-event, RMA, offline,
 * high sampling rate and Protect Plus flags.
 *
 * @param devices - Devices available to the facility.
 * @param isFacilityTest - Whether the facility is a test facility, which hides the auto-event tag.
 * @returns The sorted device options, empty when there are no devices.
 */
const handleConvertDeviceData = ({
  devices,
  isFacilityTest,
}: {
  devices: any[];
  isFacilityTest: boolean;
}) => {
  const clonedDevices = [...devices];
  clonedDevices.forEach((device) => {
    const {
      shouldDisplayHsrTag,
      shouldDisplayMaintenanceNeededTag,
      shouldDiplayRmaTag,
      shouldDisplayRenewalTag,
      shouldDisplayOfflineTag,
      shouldDisplayInStudyTag,
    } = checkDisplayTags(device);
    Object.assign(device, {
      shouldDisplayHsrTag,
      shouldDisplayMaintenanceNeededTag,
      shouldDiplayRmaTag,
      shouldDisplayRenewalTag,
      shouldDisplayOfflineTag,
      shouldDisplayInStudyTag,
    });
    if (device.status === 'Ready for new study') {
      device.orderIndex = 0;
    } else if (device.shouldDisplayMaintenanceNeededTag) {
      device.orderIndex = 1;
    } else {
      device.orderIndex = 2;
    }
  });
  /**
   * Holds the device options ordered so ready-for-new-study units come first, then maintenance-needed ones, then the rest. Produced by sorting the cloned device list on the numeric orderIndex assigned from each device's status and display tags.
   *
   * @returns The devices sorted by their assigned order index.
   */
  const sortedDevices = clonedDevices.sort((a, b) => a.orderIndex - b.orderIndex);
  if (!sortedDevices.length) {
    return [];
  }

  return sortedDevices.map((device) => {
    const {
      deviceId,
      status,
      model,
      canRunBiotresMCT,
      additionalStatus,
      isRenewal,
      shouldMaintain,
      isRunningStudy,
      lastSync,
      facility,
    } = device;

    const isBiotresPro = model === DEVICE.BIOTRES_PRO;
    const shouldDisplayAutoTag = !isFacilityTest && canRunBiotresMCT && !isBiotresPro;
    let deviceIdText = deviceId;
    const deviceModelTag = shouldDisplayAutoTag && t`Auto-event detection enabled`;
    if (deviceModelTag) {
      deviceIdText += ` (${deviceModelTag})`;
    }

    const isRma = additionalStatus === 'RMA';
    const isOffline = status === DEVICE_STATUS.OFFLINE;
    const isHighSamplingRate = checkUsingHighSamplingRateDevice(lastSync?.schema);
    const isProtectPlus = !isRma && shouldMaintain;

    return {
      label: (
        <div className="device-id-list">
          <span className="device-id">{deviceIdText}</span>
          <div className="device-label-row">
            {device.shouldDisplayHsrTag && (
              <div className="device-label device-label--hsr">{t`High sampling rate`}</div>
            )}
            {device.shouldDisplayMaintenanceNeededTag && (
              <div className="device-label device-label--protect-plus">{t`Maintenance needed`}</div>
            )}
            {device.shouldDiplayRmaTag && (
              <div className="device-label device-label--rma">{t`RMA`}</div>
            )}
            {device.shouldDisplayRenewalTag && (
              <div className="device-label device-label--renewal">{t`Renewal`}</div>
            )}
            {device.shouldDisplayOfflineTag && (
              <div className="device-label device-label--offline">{t`Offline`}</div>
            )}
            {device.shouldDisplayInStudyTag && (
              <div className="device-label device-label--in-study">{t`In study`}</div>
            )}
          </div>
        </div>
      ),
      filterLabel: deviceId,
      value: deviceId,
      operator: lastSync?.operator,
      gprs: lastSync?.gprs,
      schema: lastSync?.schema,
      model,
      isRma,
      isRenewal,
      isOffline,
      isRunningStudy,
      isProtectPlus,
      canRunBiotresMCT,
      isBiotresPro,
      isHighSamplingRate,
      shouldMaintain,
      additionalStatus,
      status: device.status,
      lastSync,
      facility,
    };
  });
};

/**
 * Resolves the ECG amplification rate to use for a device model. Reads the per-model device rate off the given signal settings when they exist, otherwise falls back to defaults of 1 for Bioflux and 2 for Biotres and Biotres Pro, keyed by the camel-cased model name.
 *
 * @param ampEcgSignal - Amplify ECG signal settings holding the per-model rates.
 * @param model - Device model whose rate is read.
 * @returns The amplification rate for that model.
 */
const getValueAmpEcg = (ampEcgSignal: AmplifyEcgSignal | null | undefined, model: string) => {
  if (!_.isNil(ampEcgSignal)) {
    return _.get(ampEcgSignal, `${_.camelCase(model)}DeviceRate`);
  }

  const initialAmpEcgSignal = {
    bioflux: 1,
    biotres: 2,
    biotresPro: 2,
  };
  return _.get(initialAmpEcgSignal, _.camelCase(model));
};

export {
  filterOption,
  checkDeviceError,
  checkCanShowModal,
  handleConvertDeviceData,
  getValueAmpEcg,
};
